import { forwardRef, Fragment, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useOutsideClickHandler } from 'hooks';

import 'components/organization/dialog/dialog.scss';

const ImportUnitNumbers = forwardRef<{ openDialog: () => void }, { handleConfirm: (unitNumbers: string[]) => void }>(
  ({ handleConfirm }, forwardedRef) => {
    const ref = useRef<HTMLDialogElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [preview, setPreview] = useState<string[]>();
    const [rawData, setRawData] = useState<string>('');

    const parsedRawData = useMemo(() => {
      const temp = rawData.replace(/[^A-Z0-9]+/g, ' ').trim();
      return !temp ? [] : temp.split(/\s/);
    }, [rawData]);

    const closeDialog = () => {
      if (!ref.current?.open) return;
      ref.current?.close();
    };

    const openDialog = () => {
      if (ref.current?.open) return;
      ref.current?.showModal();
    };

    useOutsideClickHandler(contentRef, closeDialog);

    useImperativeHandle(forwardedRef, () => ({ openDialog }), []);

    return (
      <dialog ref={ref}>
        <div className="grid-1" ref={contentRef} style={{ margin: '-12px -24px', padding: '12px 24px' }}>
          <h4>Import Unit Numbers</h4>
          {!preview ? (
            <>
              <div>
                <span>Enter data manually or&nbsp;</span>
                <span className="p-relative text-primary">
                  upload file.
                  <input
                    type="file"
                    accept=".csv, .doc, .docx, .txt"
                    className="cursor-pointer p-absolute"
                    style={{ top: 0, left: 0, bottom: 0, right: 0, minHeight: 0, opacity: 0, zIndex: 2 }}
                    onChange={e => e.target.files?.[0].text().then(text => setRawData(text.toUpperCase()))}
                  />
                </span>
              </div>
              <div className="text-field">
                <textarea className="w-100" value={rawData} onChange={e => setRawData(e.target.value.toUpperCase())} />
              </div>
              <div className="grid-3">
                <button
                  className="btn-outline col-start-2"
                  type="button"
                  onClick={() => {
                    handleConfirm([]);
                    closeDialog();
                  }}
                >
                  reset
                </button>
                <button
                  className="btn-primary col-start-3"
                  type="button"
                  disabled={!parsedRawData.length}
                  onClick={e => {
                    e.stopPropagation();
                    setPreview([...new Set(parsedRawData).values()]);
                  }}
                >
                  preview
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="overflow-auto" style={{ maxHeight: '50vh' }}>
                <dl className="grid-3 max-width-fit">
                  {preview.map((unitNumber, index) => (
                    <Fragment key={unitNumber}>
                      <dt>{index + 1}</dt>
                      <dd className="col-span-2">{unitNumber}</dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
              <div className="grid-3">
                <button
                  className="btn-outline col-start-2"
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setPreview(undefined);
                  }}
                >
                  back
                </button>
                <button
                  className="btn-primary col-start-3"
                  type="button"
                  onClick={() => {
                    handleConfirm(preview);
                    closeDialog();
                  }}
                >
                  confirm
                </button>
              </div>
            </>
          )}
        </div>
      </dialog>
    );
  }
);

export default ImportUnitNumbers;
